// Sales tax: the single source of truth for what a customer is CHARGED in tax.
// Pure + deterministic so it can be unit-tested in isolation and inlined into
// the Vercel serverless targets that cannot import workspace packages from
// source (apps/portal/api/**).
//
// Same contract as shipping.ts: the cart preview and the server charge both
// call this, so any divergence is a customer seeing one total and paying
// another.
//
// Two rules encoded here that are easy to get wrong, and wrong in a way that
// looks right:
//
//   • A non-US destination returns `none`, NOT `flat` at 0%. Applying a US
//     flat rate to a Canadian shipment produces a plausible wrong number.
//     SHIPPING_COUNTRIES is ['US','CA'], so this path is live in production.
//   • An UNMAPPED category is taxed at the general rate, never exempted.
//     Treating "unknown" as "exempt" undercharges, and the dealer cannot
//     recover it from the customer afterwards.
//
// CANONICAL COPY. Inlined into apps/portal/api/_lib/salesTax.ts (Vercel bundle
// constraint — see .claude/rules/inlined-utilities.md). Keep the inlined copy
// byte-identical (minus export/type lines) on every edit; the parity test in
// apps/portal/tests/unit/salesTaxInline.test.ts enforces it.

/** Upstream open-sales-tax taxability code. */
export type TaxCategoryCode = string

/** Applied when a line carries no category. Taxable unless the jurisdiction
 *  exempts it — never a synonym for "exempt". */
export const GENERAL_TAX_CATEGORY = 'GENERAL'

export interface TaxLineInput {
  /** Stable line id, echoed into the result so callers can label rows. */
  id: string
  /** Null means unmapped → treated as GENERAL_TAX_CATEGORY. */
  category: TaxCategoryCode | null
  /** Extended amount for the line (unit price × quantity), in dollars. */
  amount: number
  /** Units on the line. Only read by a jurisdiction with a `perItemCap`;
   *  absent = 1, so the cap applies to the whole line. */
  quantity?: number
}

export interface TaxJurisdiction {
  name: string
  /** 'state' | 'county' | 'city' | 'special' — upstream's value, not an enum
   *  we control, so it stays a string. */
  type: string
  ratePct: number
  /** Dollars of each item's price this jurisdiction taxes, when it stops
   *  short of the whole price — Florida's county surtax applies to the first
   *  $5,000 of a single item (s. 212.054(2)(b)). Absent = the whole price. */
  perItemCap?: number | null
}

/** A category a state exempts, with what the buyer is told in place of the
 *  tax (the state's own words: who collects it instead, and when). */
export interface TaxExemption {
  code: TaxCategoryCode
  note: string | null
}

export interface TaxRateStack {
  zip: string
  /** Two-letter state, needed because exemption is per-state. */
  state: string
  jurisdictions: TaxJurisdiction[]
  /** Categories this jurisdiction exempts. */
  exemptCategories: TaxCategoryCode[]
}

/** Which mode actually produced the number — what HAPPENED, not what the
 *  tenant selected, so a degraded charge is visible after the fact. */
export type TaxSource = 'none' | 'flat' | 'lookup' | 'numeral' | 'stripe_tax' | 'manual'

export interface TaxJurisdictionResult extends TaxJurisdiction {
  tax: number
}

export interface TaxLineResult {
  id: string
  taxable: boolean
  /** Tax for this line = sum of its rounded jurisdiction taxes. */
  amount: number
  jurisdictions: TaxJurisdictionResult[]
  /** Why an exempt line was not taxed, when a policy exemption zeroed it —
   *  the code and the note the buyer sees in its place. computeTax never
   *  sets this (it only knows codes); priceOrder attaches it, and it is
   *  stored per line in orders.tax_breakdown so displays read the record. */
  exemption?: TaxExemption
}

export interface TaxResult {
  total: number
  /** Total ÷ full subtotal, for the "Tax (x%)" display line. Over the FULL
   *  subtotal including exempt lines, so it never implies an exempt line was
   *  taxed. */
  blendedRatePct: number
  source: TaxSource
  lines: TaxLineResult[]
}

export interface ComputeTaxInput {
  destination: { country: string; postalCode: string | null }
  lines: TaxLineInput[]
  /** Resolved stack, or null when the ZIP did not resolve. */
  stack: TaxRateStack | null
  /** tenants.default_tax_rate — the flat fallback. */
  flatRatePct: number
  /** Categories exempt regardless of where the rate came from — e.g. a booking
   *  product the dealer marked as instruction. Unioned with a lookup stack's
   *  own exemptions. Without it, flat mode could never exempt a line. */
  exemptCategories?: TaxCategoryCode[]
  /** Exemptions that also carry what the buyer is told in place of the tax
   *  (a state's registered-vessel rule, say). Exempt like `exemptCategories`;
   *  a line one of these zeroes is labelled with it in the result. */
  exemptions?: TaxExemption[]
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** The part of a line a jurisdiction taxes: the whole amount, or — under a
 *  per-item cap — at most the cap on each unit. The amount is already net of
 *  any discount, so the cap applies to what the buyer actually pays per item. */
function taxableBase(line: TaxLineInput, j: TaxJurisdiction): number {
  if (j.perItemCap == null || !(j.perItemCap > 0)) return line.amount
  const quantity = line.quantity != null && line.quantity > 0 ? line.quantity : 1
  return Math.min(line.amount, j.perItemCap * quantity)
}

export function computeTax(input: ComputeTaxInput): TaxResult {
  const { destination, lines, stack, flatRatePct } = input

  const empty = (source: TaxSource): TaxResult => ({
    total: 0,
    blendedRatePct: 0,
    source,
    lines: lines.map(l => ({ id: l.id, taxable: false, amount: 0, jurisdictions: [] })),
  })

  // Non-US: we do not calculate, and we do not guess with a US rate.
  if (destination.country !== 'US') return empty('none')
  if (lines.length === 0) return empty('none')

  const useLookup = stack !== null && stack.jurisdictions.length > 0
  const source: TaxSource = useLookup ? 'lookup' : 'flat'

  const jurisdictions: TaxJurisdiction[] = useLookup
    ? stack.jurisdictions
    : flatRatePct > 0
      ? [{ name: 'Sales tax', type: 'flat', ratePct: flatRatePct }]
      : []

  // No jurisdiction at all (mode off, or a flat rate of 0 with no stack) means
  // nothing was applied — 'none', not 'flat' at 0%. Same distinction as the
  // non-US case: "did not calculate" must never read as "calculated zero".
  if (jurisdictions.length === 0) return empty('none')

  const exemptions = input.exemptions ?? []
  const exempt = new Set([
    ...(input.exemptCategories ?? []),
    ...exemptions.map(e => e.code),
    ...(useLookup ? stack.exemptCategories : []),
  ])

  const resultLines: TaxLineResult[] = lines.map(line => {
    const category = line.category ?? GENERAL_TAX_CATEGORY
    if (exempt.has(category)) {
      const exemption = exemptions.find(e => e.code === category)
      return {
        id: line.id,
        taxable: false,
        amount: 0,
        jurisdictions: [],
        ...(exemption ? { exemption: { code: exemption.code, note: exemption.note } } : {}),
      }
    }
    // Round per jurisdiction, then sum — so a breakdown always reconciles to
    // the charged number rather than differing by a stray cent.
    const jur = jurisdictions.map(j => ({
      ...j,
      tax: round2((taxableBase(line, j) * j.ratePct) / 100),
    }))
    const amount = round2(jur.reduce((s, j) => s + j.tax, 0))
    return { id: line.id, taxable: true, amount, jurisdictions: jur }
  })

  const total = round2(resultLines.reduce((s, l) => s + l.amount, 0))
  const subtotal = lines.reduce((s, l) => s + l.amount, 0)

  return {
    total,
    blendedRatePct: subtotal > 0 ? (total / subtotal) * 100 : 0,
    source,
    lines: resultLines,
  }
}
